import * as React from 'react';
import * as styles from './report.module.scss';
import TableauTool from '../../models/enums/tableauTool';
import RevertIcon from '../../images/icons/revert-icon';
import ExportDropdown from './export-dropdown';
import IconButtonTooltip from '../common/iconButtonTooltip';
import { IconButton } from '@material-ui/core';
import {
  Pause,
  PlayArrow,
  Refresh,
  Undo,
  Redo,
  Fullscreen,
  FullscreenExit,
  ImportExport,
  ArrowDropDown
} from '@material-ui/icons';
import { datadogRum } from '@datadog/browser-rum';

interface Props {
  viz: any;
  dashboardName: string;
  type: string;
  container?: HTMLDivElement;
  fullScreen?: boolean;
  isInteractive?: boolean;
  edge?: 'start' | 'end' | false;
}

interface State {
  isPaused: boolean;
  showViews: boolean;
  anchorEl: HTMLElement;
}

class TableauButton extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isPaused: false,
      showViews: false,
      anchorEl: null
    };
  }

  public render() {
    switch (this.props.type) {
      case TableauTool.Pause:
        return (
          <IconButtonTooltip title={this.state.isPaused ? 'Resume' : 'Pause'}>
            <IconButton onClick={this.onClick} edge={this.props.edge ? this.props.edge : false} size="small">
              {!this.state.isPaused ? <Pause /> : <PlayArrow />}
            </IconButton>
          </IconButtonTooltip>
        );
      case TableauTool.Refresh:
        return (
          <IconButtonTooltip title="Refresh">
            <IconButton onClick={this.onClick} edge={this.props.edge ? this.props.edge : false} size="small">
              <Refresh />
            </IconButton>
          </IconButtonTooltip>
        );
      case TableauTool.Revert:
        return (
          <IconButtonTooltip title="Revert">
            <IconButton onClick={this.onClick} edge={this.props.edge ? this.props.edge : false} size="small">
              <RevertIcon color="#006da8" />
            </IconButton>
          </IconButtonTooltip>
        );
      case TableauTool.Export:
        return (
          <React.Fragment>
            <div className={styles.exportButtons}>
              <IconButtonTooltip title="Export">
                <IconButton onClick={this.openExportMenu} edge={this.props.edge ? this.props.edge : false} size="small">
                  <ImportExport />
                  <ArrowDropDown className={styles.dropdown} />
                </IconButton>
              </IconButtonTooltip>
            </div>
            <ExportDropdown
              anchorEl={this.state.anchorEl}
              viz={this.props.viz}
              setAnchorEl={this.closeExportMenu}
              dashboard={this.props.dashboardName}
            />
          </React.Fragment>
        );
      case TableauTool.Undo:
        return (
          <IconButtonTooltip title="Undo">
            <IconButton onClick={this.onClick} edge={this.props.edge ? this.props.edge : false} size="small">
              <Undo />
            </IconButton>
          </IconButtonTooltip>
        );
      case TableauTool.Redo:
        return (
          <IconButtonTooltip title="Redo">
            <IconButton onClick={this.onClick} edge={this.props.edge ? this.props.edge : false} size="small">
              <Redo />
            </IconButton>
          </IconButtonTooltip>
        );
      case TableauTool.Maximize:
        return (
          <IconButtonTooltip title={this.props.fullScreen ? 'Exit Full Screen' : 'Full Screen'}>
            <IconButton onClick={this.onClick} edge={this.props.edge ? this.props.edge : false} size="small">
              {this.props.fullScreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </IconButtonTooltip>
        );
    }
  }

  private openExportMenu = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: event.currentTarget });
    datadogRum.addUserAction('export-menu', {
      user_action_details: {
        type: 'tableau_toolbar',
        name: 'export-menu',
        modal_state: 'open',
        dashboard_name: this.props.dashboardName
      }
    });
  };

  private closeExportMenu = () => {
    this.setState({ anchorEl: null });
    datadogRum.addUserAction('export-menu', {
      user_action_details: {
        type: 'tableau_toolbar',
        name: 'export-menu',
        modal_state: 'closed',
        dashboard_name: this.props.dashboardName
      }
    });
  };

  private onClick = () => {
    switch (this.props.type) {
      case TableauTool.Pause:
        datadogRum.addUserAction('pause', {
          user_action_details: {
            type: 'tableau_toolbar',
            name: 'pause',
            isPaused: !this.state.isPaused,
            dashboard_name: this.props.dashboardName
          }
        });
        this.setState({ isPaused: !this.state.isPaused });
        this.state.isPaused
          ? this.props.viz.resumeAutomaticUpdatesAsync()
          : this.props.viz.pauseAutomaticUpdatesAsync();
        break;
      case TableauTool.Revert:
        datadogRum.addUserAction('revert', {
          user_action_details: {
            type: 'tableau_toolbar',
            name: 'revert',
            dashboard_name: this.props.dashboardName
          }
        });
        this.props.viz.revertAllAsync();
        break;
      case TableauTool.Refresh:
        datadogRum.addUserAction('refresh', {
          user_action_details: {
            type: 'tableau_toolbar',
            name: 'refresh',
            dashboard_name: this.props.dashboardName
          }
        });
        this.props.viz.refreshDataAsync();
        break;
      case TableauTool.Undo:
        datadogRum.addUserAction('undo', {
          user_action_details: {
            type: 'tableau_toolbar',
            name: 'undo',
            dashboard_name: this.props.dashboardName
          }
        });
        this.props.viz.undoAsync();
        break;
      case TableauTool.Redo:
        datadogRum.addUserAction('redo', {
          user_action_details: {
            type: 'tableau_toolbar',
            name: 'redo',
            dashboard_name: this.props.dashboardName
          }
        });
        this.props.viz.redoAsync();
        break;
      case TableauTool.Maximize:
        if (this.props.fullScreen) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          }
          // @ts-ignore - IE/Edge exit full screen call
          if (document.webkitExitFullscreen) {
            // @ts-ignore - IE/Edge exit full screen call
            document.webkitExitFullscreen();
          }
        } else {
          if (this.props.container.requestFullscreen) {
            this.props.container.requestFullscreen();
          }

          // @ts-ignore - IE/Edge enter full screen call
          else if (this.props.container.webkitRequestFullscreen) {
            // @ts-ignore - IE/Edge enter full screen call
            this.props.container.webkitRequestFullscreen();
          }
        }
    }
  };
}
export default TableauButton;
